import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Col, Container, Row } from 'react-bootstrap';
import { useAlert } from 'react-alert';

import useScript from '@src/hooks/useScript';

import { IProps } from './types';
import { Loading } from '@atoms';
import ErrorDisplay from '@src/componentsNew/Error/ErrorDisplay';
import { OrderDetailFailed } from '@organisms/OrderDetailFailed/OrderDetailFailed';
import { OrderDetailInformation, OrderDetailProgress, OrderDetailSummary } from '@organisms';

import { OrderPaymentType } from '../../../../__generated__/globalTypes';
import { ORDER_BY_TOKEN, ORDER_PAYMENT_COMPLETE, CANCEL_ORDER } from '@components/pages/OrderDetailPage/queries';
import { order } from '@components/pages/OrderDetailPage/__generated__/order';
import { orderByToken } from '@components/pages/OrderDetailPage/__generated__/orderByToken';
import { orderPaymentComplete } from '@components/pages/OrderDetailPage/__generated__/orderPaymentComplete';
import { orderCancelByCustomer } from '@components/pages/OrderDetailPage/__generated__/orderCancelByCustomer';

import { gaV4BeginPayment, gaV4GetItemsFromOrder, gaV4Purchase, getDBIdFromGraphqlId } from '@utils';
import { pixelPurchase } from '@src/utils/pixel';
import { orderUrl } from '@src/app/routes';

import { load } from '@cashfreepayments/cashfree-js';

const OrderDetailPage: React.FC<IProps> = ({ match }) => {
  const history = useHistory();
  const token = match.params.token;
  const alert = useAlert();

  // State
  const [amount, setAmount] = useState(0);
  const [isValidOrder, setIsValidOrder] = useState(false);
  const [isFullyPaid, setIsFullyPaid] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isWaitingPayment, setIsWaitingPayment] = useState(false);
  const [targetPaymentId, setTargetPaymentId] = useState('');
  const [isFailedPayment, setIsFailedPayment] = useState(false);

  // https://stackoverflow.com/a/34425083
  const razorpayScriptLoaded = useScript('https://checkout.razorpay.com/v1/checkout.js');
  const firstPortOneScriptLoaded = useScript('https://cdn.jsdelivr.net/npm/axios@0.18.0/dist/axios.min.js');
  const secondPortOneScriptLoaded = useScript('https://static.portone.cloud/portone.js');

  const { data, error, loading } = useQuery<orderByToken>(ORDER_BY_TOKEN, {
    variables: { token: token },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setAmount(data.orderByToken.total.gross.amount);
      validateOrder(data.orderByToken);
    },
  });

  const iteratePayments = async (order: order) => {
    let totalPaid = 0;
    order.payments.forEach((p) => {
      if (p.chargeStatus === 'CHARGED') {
        totalPaid += p.capturedAmount.amount;
      }
      if (p.isActive) {
        if (!isAuthorized) {
          const someAuthorized = p.transactions.some((t) => {
            return t.kind === 'AUTH' && t.isSuccess;
          });
          setIsAuthorized(someAuthorized);
        }
        if (p.chargeStatus === 'NOT_CHARGED') {
          const someWaitingPayment = p.transactions.some((t) => {
            return t.kind === 'AUTH';
          });
          if (someWaitingPayment) {
            setIsWaitingPayment(true);
          } else {
            setTargetPaymentId(p.id);
          }
        }
      }
    });
    if (order.total.gross.amount <= totalPaid) {
      setIsFullyPaid(true);
    }
  };

  const validateOrder = async (order: order) => {
    // wait for the all evaluations since setState is async
    await iteratePayments(order);

    if (
      order.status === 'CANCELED' ||
      order.paymentStatus === 'FULLY_REFUNDED' ||
      !order.billingAddress ||
      isFullyPaid ||
      isAuthorized ||
      isWaitingPayment
    ) {
      setIsValidOrder(false);
    } else {
      setIsValidOrder(true);
    }
  };

  const [completeOrderPayment, { loading: loadingComplete }] = useMutation<orderPaymentComplete>(
    ORDER_PAYMENT_COMPLETE,
  );

  const handlePayment = (e: any) => {
    if (data.orderByToken.paymentType == OrderPaymentType.PREPAID) {
      handleRazorpayPayment(e);
    } else if (data.orderByToken.paymentType == OrderPaymentType.SIMPL) {
      handleSimplPayment(e);
    } else if (data.orderByToken.paymentType == OrderPaymentType.TAZAPAY_CARD) {
      handleTazapayPayment(e);
    } else if (data.orderByToken.paymentType == OrderPaymentType.CASHFREE) {
      handleCashfreePayment(e);
    }
  };

  const handleSimplPayment = (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!isValidOrder || !targetPaymentId) {
      //TODO: Create a new payment if target payment doesn't exist.
      return;
    }

    const order = data.orderByToken;
    const redirectUrl = order.simplInitiateUrl ?? '';
    if (redirectUrl.length > 0) {
      location.href = redirectUrl;
    }
  };

  const handleTazapayPayment = (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!isValidOrder || !targetPaymentId) {
      return;
    }

    const order = data.orderByToken;
    if (order.paymentRedirectionUrl) {
      location.href = order.paymentRedirectionUrl;
    }
  };

  const handleCashfreePayment = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!isValidOrder || !targetPaymentId) {
      return;
    }

    const cashfree = await load({
      mode: process.env.ENVIRONMENT === 'production' ? 'production' : 'sandbox',
    });

    const order = data.orderByToken;
    const paymentSessionId = order.paymentSessionId;

    if (!paymentSessionId) {
      console.error('Payment Session ID is Missing.');
      return;
    }

    const checkoutOptions = {
      paymentSessionId: paymentSessionId,
      redirectTarget: '_modal',
    };

    cashfree.checkout(checkoutOptions).then((result) => {
      if (result.error) {
        history.push({
          pathname: `${orderUrl}${token}`,
          state: order,
        });
      }

      if (result.paymentDetails) {
        if (order) {
          pixelPurchase(order, 'INR');
          gaV4Purchase(order);
          history.push({
            pathname: `${orderUrl}${token}/checkout-success/`,
            state: order,
          });
        }
      }
    });
  };

  const handleRazorpayPayment = (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!isValidOrder || !targetPaymentId) {
      return;
    }

    type razorpayResponseType = {
      razorpay_payment_id: string;
      razorpay_order_id: string;
      razorpay_signature: string;
    };
    const order = data.orderByToken;
    const firstName = order.billingAddress.firstName;
    const lastName = order.billingAddress.lastName;
    const email = order.userEmail;
    const orderId = order.id;
    const contact = order.shippingAddress.phone;
    const razorpayApiKey = process.env.RAZORPAY_API_KEY;
    const options = {
      key: razorpayApiKey,
      amount: Math.round(amount * 100),
      name: 'Total payment',
      handler: (response: razorpayResponseType) => {
        if (response.razorpay_payment_id) {
          completeOrderPayment({
            variables: {
              orderId: orderId,
              paymentId: targetPaymentId,
              paymentToken: response.razorpay_payment_id,
            },
          }).then((res) => {
            const order = res?.data?.orderPaymentComplete?.order;
            if (order) {
              pixelPurchase(order, 'INR');
              gaV4Purchase(order);
              history.push({
                pathname: `${orderUrl}${token}/checkout-success/`,
                state: order,
              });
            }
          });
        }
      },
      prefill: {
        name: `${firstName} ${lastName}`,
        email: email,
        contact: contact,
      },
    };

    const items = gaV4GetItemsFromOrder(order);
    const value = order?.total?.gross?.amount ?? 0;
    const usedPoint = order?.usedPoint?.amount ?? 0;
    const discount = order?.discountAmount?.amount ?? 0;
    const coupon = order?.voucher?.name ?? '';
    gaV4BeginPayment(
      items,
      value,
      usedPoint,
      discount, // discountAmount by voucher
      coupon,
    );

    //@ts-ignore
    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  const [cancelOrder] = useMutation<orderCancelByCustomer>(CANCEL_ORDER, {
    onCompleted: (data) => {
      const errors = data.orderCancelByCustomer.errors;
      // if ((errors?.length ?? 0) > 0) {
      //   const msg = joinErrorMessages(errors);
      //   alert.error(msg);
      // }
    },
  });

  const handleCancelOrder = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    const order = data.orderByToken;
    const result = confirm('Do you want to cancel the order # ' + getDBIdFromGraphqlId(order.id));
    if (result) {
      // cancel mutation
      const result = await cancelOrder({ variables: { id: order.id } });
      if (result.data.orderCancelByCustomer.errors) {
        alert.show(result.data.orderCancelByCustomer.errors[0].message, {
          timeout: 5000,
          type: 'success',
        });
      } else {
        alert.show('Order # ' + getDBIdFromGraphqlId(order.id) + ' has been canceled.', {
          onClose() {
            window.location.reload();
          },
          timeout: 5000,
          type: 'success',
        });
      }
    }
  };

  useEffect(() => {
    if (
      isValidOrder &&
      targetPaymentId &&
      razorpayScriptLoaded &&
      firstPortOneScriptLoaded &&
      secondPortOneScriptLoaded
    ) {
      const searchParams = new URLSearchParams(document.location.search);
      const simplStatus = searchParams.get('status');

      if (simplStatus !== 'FAILED') {
        handlePayment(null);
      } else {
        // 결제 실패 화면 보여주기
        setIsFailedPayment(true);
      }
    }
  }, [isValidOrder, targetPaymentId, razorpayScriptLoaded]);

  const handleTryAgain = () => {
    setIsFailedPayment(false);
  };

  if (loading) {
    return <Loading />;
  }

  return data?.orderByToken ? (
    isFailedPayment ? (
      <OrderDetailFailed handleTryAgain={handleTryAgain} handleCancelOrder={handleCancelOrder}></OrderDetailFailed>
    ) : (
      <div id="order-detail-wrapper">
        <OrderDetailProgress
          orderByToken={data.orderByToken}
          handlePayment={handlePayment}
          handleCancelOrder={handleCancelOrder}
        />
        <Container>
          <Row>
            <Col md={4} xs={12}>
              <OrderDetailSummary orderByToken={data.orderByToken} />
            </Col>
            <Col md={8} xs={12}>
              <OrderDetailInformation orderByToken={data.orderByToken} />
            </Col>
          </Row>
        </Container>
      </div>
    )
  ) : (
    <ErrorDisplay />
  );
};

export { OrderDetailPage };
